import { AsyncPipe, CommonModule, UpperCasePipe } from '@angular/common';
import {
  Component, ElementRef, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';

import { debounceTime, distinctUntilChanged, filter, Observable, Subject, Subscription, switchMap, tap } from 'rxjs';
import { UserService } from 'src/app/core/services/lmsuser.service';
import { IsLoadingService } from 'src/app/features/branch-employers/services/is-loading.service';
import { BranchEmployerServiceNew } from 'src/app/features/service/branch-employer.service';

import { HighlightDirective, NoSpecialCharPipe, UpperCaseSearchListPipe } from '@lmsSharedUI';

import { HighlightSearchPipe } from '../../../shared/pipes/highlight-search.pipe';
import { BranchEmployer } from '../models/branch-employers';

@Component({
  selector: 'app-branch-employers-search',
  imports: [
    CommonModule,
    AsyncPipe,
    HighlightDirective,
    UpperCasePipe,
    NoSpecialCharPipe,
    UpperCaseSearchListPipe,
    HighlightSearchPipe
],
  templateUrl: './basic-employer-form.component.html',
  standalone: true,
  styleUrls: ["./basic-employer-form.component.scss"],
  // changeDetection: ChangeDetectionStrategy.OnPush
})
export class BasicEmployersSearchComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() dontAdd = false;
  @Input() searchValue = '';
  @Input() debounceTime = 700;
  @Input() showAddButton = false;
  @Input() frmControlName!: string;

  @Output() textChange = new EventEmitter<string>();
  @Output() addnewRequired = new EventEmitter<boolean>();
  @Output() empSelected = new EventEmitter<any>();

  @ViewChild('searchValue') searchInputRef?: ElementRef<HTMLInputElement>;

  // Core state properties
  employer$: Observable<any[]>;
  isLoading$: Observable<boolean>;
  highlightedIndex = -1;
  isInputFocused = false;
  currentSearchTerm: string = '';
  currentEmployers: any[] = [];

  // Current selection state
  selectedEmployer = {
    id: 0,
    employerName: '',
    tnumber: 0,
    name: '',
  };

  private employerNameSubject = new Subject<string>();
  private subscriptions: Subscription[] = [];
  private _branchSerial: string;

  private userService = inject(UserService);
  private branchEmployersService = inject(BranchEmployerServiceNew);
  private isLoadingService = inject(IsLoadingService);
  quickSearchPending$: any;
  bEmployers: any;
  selected_branchEmployer: any = {
    id: 0,
    employerName: '',
    tnumber: 0,
    name: '',
  };

  constructor(public fb: FormBuilder) {
    this.employer$ = this.employerNameSubject.pipe(
      distinctUntilChanged(),
      filter((name) => name.trim().length > 0),
      tap(() => this.highlightedIndex = -1),
      tap((value) => {
        // Store current search term for reference
        this.currentSearchTerm = value;
      }),
      debounceTime(this.debounceTime),
      switchMap((value: string) =>
        this.isLoadingService.add(
          this.branchEmployersService.quickSearchBranchEmployers({
            branchSerial: this._branchSerial || this.userService.userBranchSerial,
            ename: value.toUpperCase(),
          }).pipe(
            tap((results) => {
              // Store current employers for keyboard navigation
              this.currentEmployers = results || [];
            }),
          ),
        ),
      ),
    );
    this.isLoading$ = this.isLoadingService.isLoading$();
  }

  searchResults: [] = [];
  showSuggestions: boolean = false;
  currentSearchText: string = '';

  ngOnInit() {
    this._branchSerial = this.userService.userBranchSerial;

    this.selectedEmployer = {
      id: 0,
      employerName: this.searchValue?.toUpperCase() || '',
      tnumber: 0,
      name: this.searchValue?.toUpperCase() || '',
    };

    const subscription = this.employerNameSubject.subscribe((searchTerm) => {
      this.currentSearchTerm = searchTerm;
      this.textChange.emit(searchTerm.toUpperCase());
    });
    this.subscriptions.push(subscription);

    const quickSearchSubscription = this.employer$.subscribe((results: any) => {
      this.searchResults = results || [];
      // Keep dropdown open while user is typing; don't hide on transient empty result sets.
      this.showSuggestions = this.selected_branchEmployer.tnumber === 0 && this.currentSearchText.length > 0;
    });
    this.subscriptions.push(quickSearchSubscription);

    // Note, because `IsLoadingService#isLoading$()` returns
    // a new observable each time it is called, it shouldn't
    // be called directly inside a component template.
    this.quickSearchPending$ = this.isLoadingService.isLoading$();

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && !changes.searchValue.firstChange) {
      if (changes.searchValue.currentValue != '' && changes.searchValue.currentValue.includes(',')) {

        this.selected_branchEmployer = {
          id: Number(changes.searchValue.currentValue.split(',')[1]),
          employerName: changes.searchValue.currentValue.split(',')[0],
          name: changes.searchValue.currentValue.split(',')[0],
          tnumber: Number(changes.searchValue.currentValue.split(',')[1])
        };
        this.selectedEmployer = this.selected_branchEmployer;

        const searchTxt = document.querySelector("#searchValue");
        if (searchTxt) {
          (searchTxt as HTMLInputElement).value = changes.searchValue.currentValue == 'SASSA,0' ? '' : changes.searchValue.currentValue.split(',')[0];
        }
      }
    }
  }

   employersearchFormGroup: any;
   branchEmployers: any[] = []

  private createForm() {
    this.employersearchFormGroup = this.fb.group({
      employer: ['', [Validators.required, Validators.pattern('^[a-zA-Z -]*$')]],
      selemployerName: [this.branchEmployers, Validators.required, Validators.pattern('^[a-zA-Z., -]*$')]
    });
  }

  searchEmployersByName(filterValue: string) {
    filterValue = filterValue.replace(" ", "").toUpperCase();
    return this.bEmployers.filter(
      (employer) =>
        employer.employerName
          .replace(" ", "")
          .toUpperCase()
          .indexOf(filterValue) !== -1
    );
  }
  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  // Push a search term into the observable stream
  quickSearch(searchElement: HTMLInputElement | null) {
    if (!searchElement) {
      return;
    }

    const searchTerm = (searchElement.value ?? '').trim();

    this.selectedEmployer = {
      id: 0,
      employerName: searchTerm,
      tnumber: 0,
      name: searchTerm,
    };

    this.employerNameSubject.next(searchTerm);

    if (searchTerm.length === 0) {
      this.highlightedIndex = -1;
    }
  }

  onInputFocus() {
    this.isInputFocused = true;
  }

  onInputBlur() {
    setTimeout(() => {
      this.isInputFocused = false;
      this.highlightedIndex = -1;
    }, 120);
  }

  onKeyDown(event: KeyboardEvent, employers: any[]) {
    const list = employers || [];
    if (!list.length) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlightedIndex = Math.min(this.highlightedIndex + 1, list.length - 1);
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
        break;

      case 'Enter':
        if (this.highlightedIndex >= 0) {
          event.preventDefault();
          this.employerSelected(list[this.highlightedIndex]);
          this.highlightedIndex = -1;
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.highlightedIndex = -1;
        this.isInputFocused = false;
        this.searchInputRef?.nativeElement.blur();
        break;
    }
  }

  onKeyDownHandler(event: KeyboardEvent) {
    // Use stored current employers for keyboard navigation
    this.onKeyDown(event, this.currentEmployers);
  }

  trackByEmployer(index: number, employer: BranchEmployer): string | number {
    return employer?.tnumber ?? employer?.id ?? employer?.name ?? index;
  }

  getEmployerLabel(employer: BranchEmployer): string {
    return employer?.name || employer?.employerName || '';
  }

  isDropdownVisible(): boolean {
    const isUnselected = this.selectedEmployer.tnumber === 0;
    const hasSearchValue = (this.selectedEmployer.name || '').trim().length > 0;
    return isUnselected && hasSearchValue && this.isInputFocused;
  }

  onAddNewClick() {
    this.addnewRequired.emit(true);
  }

  employerSelected(employer?: BranchEmployer) {
    if (!employer) {
      this.empSelected.emit({
        employerName: this.searchValue,
        id: 0,
        name: this.searchValue,
        tnumber: 0,
      });
      return;
    }

    if (employer.id === -99) {
      this.addnewRequired.emit(true);
      return;
    }

    const label = this.getEmployerLabel(employer);
    const id = employer?.tnumber ?? employer?.id ?? 0;

    // Update the input value safely
    if (this.searchInputRef?.nativeElement) {
      this.searchInputRef.nativeElement.value = label;
      // Dispatch input event to keep internal state in sync
      const inputEvent = new Event('input', { bubbles: true });
      this.searchInputRef.nativeElement.dispatchEvent(inputEvent);
    }

    this.selectedEmployer = {
      id,
      employerName: label,
      name: label,
      tnumber: id,
    };

    this.isInputFocused = false;
    this.highlightedIndex = -1;
    this.empSelected.emit(this.selectedEmployer);
  }

}
